import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { TranslateQuestionnaireContent } from '../../shared/TranslateQuestionnaire/TranslateQuestionnaireContent';

import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { ReviewQuestionnaire } from '@/types/review/ReviewQuestionnaire';
import type { LocalizedText } from '@/utils/localization';

import { useGetCycleInstance } from '@/api/cycle-evaluation-services/cycle-instance';
import { useGetUsersByIds } from '@/api/directory';
import { useUpdateReviewQuestionnaire } from '@/api/review-services/review-questionnaire';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { getReviewFormRouteName } from '@/navigation/utils';
import {
    createTranslateTitleSchema,
    type TranslateTitleFormData,
} from '@/pages/shared/QuestionnaireCreation/validation';
import { showSuccessToast, showWarningToast } from '@/plugins/Toast';
import { getAllCycleInstanceAssignedUserIds } from '@/utils/cycleInstance';
import { getReviewees } from '@/utils/reviewAudience';
import { validationMessageKey } from '@/utils/validationMessage';

export const TranslateQuestionnairePage = () => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const { handleError } = useError();
    const location = useLocation<{ cycleInstanceId?: string; step?: string }>();

    const cycleInstanceId = location.state?.cycleInstanceId || '';
    const step = location.state?.step || '';

    const { data: cycleInstance, isLoading } = useGetCycleInstance(cycleInstanceId, {
        includeInactiveQuestions: true,
    });
    const { mutateAsync: updateQuestionnaire, isPending: isSaving } =
        useUpdateReviewQuestionnaire();

    const userIds = useMemo(
        () =>
            cycleInstance ? Array.from(getAllCycleInstanceAssignedUserIds([cycleInstance])) : [],
        [cycleInstance],
    );
    const { data: users } = useGetUsersByIds({
        userIds,
        identifier: 'translate-questionnaire-360',
    });
    const previewEvaluee = useMemo(
        () => getReviewees({ reviewAudience: cycleInstance?.reviewAudience, users })[0],
        [cycleInstance?.reviewAudience, users],
    );

    const questions = cycleInstance?.reviewQuestionnaire?.reviewQuestions || [];

    const [targetLanguage, setTargetLanguage] = useState<string>('');

    const translateTitleSchema = useMemo(
        () => createTranslateTitleSchema(targetLanguage),
        [targetLanguage],
    );

    // TODO: The title input is managed as localTitle in TranslateQuestionnaireContent; useForm here is
    // used only as a validation trigger (setValue → trigger → errors). Intentional — more translatable
    // fields are planned. When added, consider extracting this into a shared useTranslatePage hook.
    const {
        watch,
        setValue,
        reset,
        trigger,
        formState: { errors },
    } = useForm<TranslateTitleFormData>({
        resolver: zodResolver(translateTitleSchema),
        defaultValues: { title: {} },
    });

    const formTitle = watch('title');

    const onTargetLanguageChange = useCallback((language: string) => {
        setTargetLanguage(language);
    }, []);

    useEffect(() => {
        if (targetLanguage) {
            reset({ title: formTitle });
        }
    }, [targetLanguage]);

    const onSave = async (updatedQuestions: ReviewQuestion[], updatedTitle?: LocalizedText) => {
        if (!cycleInstance?.reviewQuestionnaire) return;

        const titleToSave = updatedTitle ?? cycleInstance.reviewQuestionnaire.title;
        setValue('title', titleToSave);

        const isValid = await trigger('title');
        if (!isValid) {
            const errorMessage = errors.title?.title?.message;
            if (errorMessage) {
                return showWarningToast({ message: t(validationMessageKey(errorMessage)) });
            }
        }

        try {
            const updatedQuestionnaire: ReviewQuestionnaire = {
                id: cycleInstance.reviewQuestionnaire.id,
                title: titleToSave,
                reviewTemplateId: cycleInstance.reviewQuestionnaire.reviewTemplateId,
                reviewQuestions: updatedQuestions,
                cycleInstanceId: cycleInstance.id,
            };

            await updateQuestionnaire(updatedQuestionnaire);
            showSuccessToast({
                message: t('templateManagement.templateTranslation.message.saveSuccess'),
            });
        } catch (error) {
            handleError({ error });
        }
    };

    const onBack = () => {
        const routeName = getReviewFormRouteName(cycleInstance?.reviewType);

        navigate({
            path: `${routeName}/${step}/${cycleInstanceId}`,
        });
    };

    return (
        <TranslateQuestionnaireContent
            questions={questions}
            isLoading={isLoading}
            isSaving={isSaving}
            onSave={onSave}
            onBack={onBack}
            backLabel={t('common.backToQuestionnaire')}
            title={t('templateManagement.questionnaireTranslation.title')}
            subtitle={t('templateManagement.questionnaireTranslation.subtitle')}
            evaluationTitle={cycleInstance?.reviewQuestionnaire?.title}
            titleError={errors.title?.title?.message as string | undefined}
            onTargetLanguageChange={onTargetLanguageChange}
            previewEvaluee={previewEvaluee}
        />
    );
};
